import type { BankAccountGroupSchema, BankAccountGroupStatementMemberSchema } from "bankconnect";
import { useState } from "react";

interface UseReassignedStatementsForGroupProps {
  accountGroups: BankAccountGroupSchema[];
  originGroupUuid: string | null;
}

export interface ReassignedStatementMember {
  member: BankAccountGroupStatementMemberSchema;
  destinationGroup: BankAccountGroupSchema;
}

interface UseReassignedStatementsForGroupResult {
  reassignedMembers: ReassignedStatementMember[];
  recordUploadAttempt: () => void;
  clear: () => void;
}

// The statement twin of useReassignedConnectionsForGroup, minus its localStorage persistence -
// statement upload never redirects off-page, so there's no reload to survive.
export const useReassignedStatementsForGroup = ({
  accountGroups,
  originGroupUuid,
}: UseReassignedStatementsForGroupProps): UseReassignedStatementsForGroupResult => {
  const [memberUuidsBeforeUpload, setMemberUuidsBeforeUpload] = useState<Set<string> | null>(null);

  const recordUploadAttempt = (): void => {
    setMemberUuidsBeforeUpload((current) => {
      if (current === null) {
        const allMemberUuids = accountGroups.flatMap((group) =>
          group.statementMembers.map((member) => member.memberUuid)
        );
        return new Set(allMemberUuids);
      }
      const originMemberUuids =
        accountGroups
          .find((group) => group.uuid === originGroupUuid)
          ?.statementMembers.map((member) => member.memberUuid) ?? [];
      return new Set([...current, ...originMemberUuids]);
    });
  };

  const clear = (): void => setMemberUuidsBeforeUpload(null);

  const reassignedMembers: ReassignedStatementMember[] =
    memberUuidsBeforeUpload && originGroupUuid
      ? accountGroups.flatMap((group) =>
          group.uuid === originGroupUuid
            ? []
            : group.statementMembers
                .filter((member) => !memberUuidsBeforeUpload.has(member.memberUuid))
                .map((member) => ({ member, destinationGroup: group }))
        )
      : [];

  return { reassignedMembers, recordUploadAttempt, clear };
};
